/**
 * ACT-QUICK-REPLY-001 — the action buttons under the in-app welcome.
 *
 * The backend decides WHICH message carries buttons (it stamps the welcome when it sends it) and
 * returns them as `quickReplies`; this file decides which of them this person, on this screen, can
 * actually use, and where each one goes. A button is navigation only: it never sends a message,
 * so it is not a reply to the support agent and changes nothing about the proactive touches.
 */

import { canCreateProducts } from './productDraftPrefill'

export const QUICK_REPLY_IDS = ['add_product', 'new_sale'] as const
export type QuickReplyId = (typeof QUICK_REPLY_IDS)[number]

const isQuickReplyId = (value: unknown): value is QuickReplyId =>
  typeof value === 'string' && (QUICK_REPLY_IDS as readonly string[]).includes(value)

/**
 * The known button ids from a server payload, in server order and without repeats. An id this
 * build does not know is dropped rather than rendered as a button that goes nowhere. Null when
 * nothing usable remains.
 */
export const normalizeQuickReplies = (raw: unknown): QuickReplyId[] | null => {
  if (!Array.isArray(raw)) return null
  const known = [...new Set(raw.filter(isQuickReplyId))]
  return known.length > 0 ? known : null
}

interface QuickReplyAccess {
  userRole: { id?: string; permissions?: Record<string, unknown> } | null | undefined
  user: { role?: string } | null | undefined
  organization: { moduleEnabled?: Record<string, unknown> } | null | undefined
  /** `organizationSubscriptionInfoAtom`: the home menu hides its tiles while `isBlocked`. */
  subscription?: { isBlocked?: boolean } | null
}

/**
 * Whether this person can use a button. The same rules as the screens the buttons open:
 * creating a product needs the `Products` permission (the product screen's and the backend's rule,
 * as for ATL-1323's drafts); a new sale needs `NewSale`, the `sales` module and an organization
 * that is not over its billing block, exactly as the home menu shows its "Новая продажа" tile.
 */
export const canUseQuickReply = (reply: QuickReplyId, access: QuickReplyAccess): boolean => {
  if (reply === 'add_product') return canCreateProducts(access.userRole, access.user)
  const admin = access.userRole?.id === 'admin' || access.user?.role === 'admin'
  return Boolean(
    (admin || access.userRole?.permissions?.['NewSale']) &&
      access.organization?.moduleEnabled?.['sales'] &&
      !access.subscription?.isBlocked
  )
}

/** Where a button goes, as the arguments to `navigation.navigate`. */
export const quickReplyNavigation = (
  reply: QuickReplyId
): { name: string; key: string; params?: Record<string, unknown> } =>
  reply === 'add_product'
    ? // Create mode, as the Products screen's own "+" opens it. Keyed so a tap while a product is
      // already open starts a fresh form instead of swapping the open one's params.
      { name: 'ProductDetail', key: 'support-quick-reply-add-product', params: { productItemProp: null } }
    : // The home menu's own navigation to a new sale.
      { name: 'NewSale', key: 'NewSale' }
